import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, Loader2, Plus, SkipForward } from "lucide-react";
import { adminApi, apiError } from "../adminApi";
import { Panel, buttonClass, inputClass } from "../components/Bits";

export const CAMPAIGN_STYLE = {
  plant: { label: "Plant", chip: "border-sage/50 bg-sage/10 text-sage" },
  grow: { label: "Grow", chip: "border-[#5B9DD9]/50 bg-[#5B9DD9]/10 text-[#8FC3F0]" },
  replant: { label: "Replant", chip: "border-gold/50 bg-gold/10 text-gold" },
  none: { label: "None", chip: "border-cream/15 bg-cream/5 text-cream/50" },
};

export const CampaignChip = ({ id }) => {
  const style = CAMPAIGN_STYLE[id] || CAMPAIGN_STYLE.none;
  return (
    <span className={`rounded-full border px-2.5 py-0.5 text-[11px] ${style.chip}`}>
      {style.label}
    </span>
  );
};

export const CampaignPicker = ({ deal, campaigns, onPick, compact = false }) => {
  const suggestion = deal.suggested_campaign;
  return (
    <div className="space-y-1.5">
      <select
        value={deal.nurture_campaign || "none"}
        data-testid={`campaign-select-${deal.id}`}
        onChange={(event) => onPick(deal, event.target.value)}
        className={`${inputClass} ${compact ? "py-1.5 text-xs" : ""}`}
      >
        {campaigns.map((campaign) => (
          <option key={campaign.campaign_id} value={campaign.campaign_id}>
            {campaign.name}
          </option>
        ))}
      </select>
      {suggestion && suggestion !== (deal.nurture_campaign || "none") ? (
        <button type="button" onClick={() => onPick(deal, suggestion)}
                data-testid={`campaign-hint-${deal.id}`}
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream/35 transition-colors duration-200 hover:text-gold">
          Suggested: {CAMPAIGN_STYLE[suggestion].label} — use it
        </button>
      ) : null}
      {deal.medicare ? (
        <p className="text-[10px] leading-relaxed text-terra" data-testid={`medicare-note-${deal.id}`}>
          Medicare prospects: inbound/permission-based outreach only.
        </p>
      ) : null}
    </div>
  );
};

const DealCard = ({ deal, campaigns, onPick, onOpen }) => (
  <li className="rounded-xl border border-cream/10 bg-[#10211A] p-4" data-testid={`deal-card-${deal.id}`}>
    <button type="button" onClick={() => onOpen(deal)} className="text-left"
            data-testid={`deal-open-${deal.id}`}>
      <p className="text-sm leading-snug text-cream/90 transition-colors duration-200 hover:text-gold">
        {deal.name}
      </p>
    </button>
    {deal.account ? (
      <Link to={`/admin/accounts/${deal.account_id}`}
            className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-cream/30 transition-colors duration-200 hover:text-cream/70">
        {deal.account.name}
      </Link>
    ) : null}

    <div className="mt-3 flex items-center gap-2">
      <CampaignChip id={deal.nurture_campaign || "none"} />
      {deal.next_touch ? (
        <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${
          deal.overdue ? "text-terra" : "text-cream/45"}`}
              data-testid={`deal-next-${deal.id}`}>
          {deal.next_touch.step_id} · {deal.next_touch.channel} · due{" "}
          {new Date(`${deal.next_touch.due_date}T12:00:00`).toLocaleDateString("en-US",
            { month: "short", day: "numeric" })}
        </span>
      ) : null}
    </div>

    <div className="mt-3">
      <CampaignPicker deal={deal} campaigns={campaigns} onPick={onPick} compact />
    </div>
  </li>
);

const TouchRow = ({ touch, onDone, onSkip }) => (
  <li className="flex flex-wrap items-start justify-between gap-3 py-3"
      data-testid={`touch-${touch.id}`}>
    <div className="min-w-0">
      <p className="text-sm text-cream/90">{touch.deal_name}</p>
      <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/35">
        {touch.step_id} · {touch.channel} · {touch.automation} ·{" "}
        <span className={touch.overdue ? "text-terra" : ""}>due {touch.due_date}</span>
      </p>
      <p className="mt-1.5 max-w-2xl text-xs leading-relaxed text-cream/50">{touch.content}</p>
      {touch.subject ? (
        <p className="mt-1 text-xs text-cream/70">{touch.subject}</p>
      ) : null}
    </div>
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => onDone(touch)} data-testid={`touch-done-${touch.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-sage/40 px-4 py-2 text-xs text-sage transition-colors duration-200 hover:bg-sage/10">
        <Check className="h-3.5 w-3.5" /> Mark done
      </button>
      <button type="button" onClick={() => onSkip(touch)} data-testid={`touch-skip-${touch.id}`}
              className="inline-flex items-center gap-1.5 text-xs text-cream/40 transition-colors duration-200 hover:text-terra">
        <SkipForward className="h-3.5 w-3.5" /> Skip
      </button>
    </div>
  </li>
);

const NewDealForm = ({ onCreated }) => {
  const [form, setForm] = useState({ name: "", account_id: "", stage: "new",
                                     renewal_date: "", target_effective_date: "",
                                     medicare: false });
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async (term) => {
    if (term.length < 2) return setAccounts([]);
    const { data } = await adminApi.get("/crm/accounts",
                                        { params: { search: term, page_size: 8 } });
    setAccounts(data.accounts);
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((key) => { if (payload[key] === "") delete payload[key]; });
      await adminApi.post("/crm/deals", payload);
      toast.success("Deal created");
      setForm({ name: "", account_id: "", stage: "new", renewal_date: "",
                target_effective_date: "", medicare: false });
      setAccounts([]);
      onCreated();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2" data-testid="new-deal-form">
      <input required className={inputClass} placeholder="Deal name" value={form.name}
             data-testid="deal-name-input"
             onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <div>
        <input className={inputClass} placeholder="Attach an account — search by name"
               data-testid="deal-account-search" onChange={(e) => search(e.target.value)} />
        {accounts.length ? (
          <ul className="mt-2 max-h-40 overflow-auto rounded-lg border border-cream/10">
            {accounts.map((account) => (
              <li key={account.id}>
                <button type="button" data-testid={`deal-account-pick-${account.id}`}
                        onClick={() => {
                          setForm((prev) => ({ ...prev, account_id: account.id,
                                               name: prev.name || `${account.name} — benefits` }));
                          setAccounts([]);
                        }}
                        className={`w-full px-3 py-2 text-left text-xs transition-colors duration-200 hover:bg-cream/5 ${
                          form.account_id === account.id ? "text-gold" : "text-cream/70"}`}>
                  {account.name}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {form.account_id ? (
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-sage"
             data-testid="deal-account-attached">Account attached</p>
        ) : null}
      </div>
      <input type="date" className={inputClass} value={form.renewal_date}
             data-testid="deal-renewal-input" title="Renewal date"
             onChange={(e) => setForm({ ...form, renewal_date: e.target.value })} />
      <input type="date" className={inputClass} value={form.target_effective_date}
             data-testid="deal-effective-input" title="Target effective date"
             onChange={(e) => setForm({ ...form, target_effective_date: e.target.value })} />
      <label className="flex items-center gap-2 text-xs text-cream/60">
        <input type="checkbox" checked={form.medicare} data-testid="deal-medicare-input"
               onChange={(e) => setForm({ ...form, medicare: e.target.checked })} />
        Medicare prospect (blocked from nurture campaigns)
      </label>
      <button type="submit" disabled={busy} className={buttonClass} data-testid="deal-create-submit">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
        Create deal
      </button>
    </form>
  );
};

export default function AdminPipeline() {
  const [board, setBoard] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [touches, setTouches] = useState({ touches: [], overdue: 0, due_today: 0, upcoming: 0 });
  const [stalled, setStalled] = useState([]);
  const [filter, setFilter] = useState("");
  const [view, setView] = useState("board");
  const [openDeal, setOpenDeal] = useState(null);

  const load = useCallback(async () => {
    const [b, c, t, s] = await Promise.allSettled([
      adminApi.get("/crm/deals", { params: filter ? { campaign: filter } : {} }),
      adminApi.get("/crm/nurture/campaigns"),
      adminApi.get("/crm/nurture/touches", { params: { when: "today" } }),
      adminApi.get("/crm/nurture/stalled"),
    ]);
    if (b.status === "fulfilled") setBoard(b.value.data);
    if (c.status === "fulfilled") setCampaigns(c.value.data.campaigns);
    if (t.status === "fulfilled") setTouches(t.value.data);
    if (s.status === "fulfilled") setStalled(s.value.data.stalled);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const pick = async (deal, campaignId) => {
    let renewal;
    if (campaignId === "replant" && !deal.renewal_date) {
      renewal = window.prompt(
        `Replant works backwards from the renewal. When does ${deal.name} renew? (YYYY-MM-DD)`);
      if (!renewal) return;
    }
    try {
      const { data } = await adminApi.post(`/crm/deals/${deal.id}/campaign`,
                                           { campaign_id: campaignId, renewal_date: renewal });
      toast.success(campaignId === "none"
        ? `Campaign cleared, ${data.cancelled} touches cancelled`
        : `${data.scheduled.length} touches scheduled`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const closeTouch = async (touch, action) => {
    try {
      await adminApi.post(`/crm/nurture/touches/${touch.id}/${action}`, {});
      toast.success(action === "done" ? "Logged — next touch scheduled" : "Touch skipped");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!board) {
    return <div className="py-20 text-center text-sm text-cream/40" data-testid="pipeline-loading">
      Loading the pipeline…
    </div>;
  }

  return (
    <div className="space-y-7" data-testid="admin-pipeline-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Pipeline</p>
          <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
            Every deal, on a campaign
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-cream/50">
            Pick Plant, Grow or Replant and the desk schedules each touch, raises your tasks, and
            moves the deal between campaigns as it replies, stalls or signs.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {["board", "touches"].map((option) => (
            <button key={option} type="button" onClick={() => setView(option)}
                    data-testid={`pipeline-view-${option}`}
                    className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                      view === option ? "border-gold/50 text-gold"
                        : "border-cream/15 text-cream/45 hover:text-cream"}`}>
              {option === "board" ? "Board" : `Today's touches (${touches.touches.length})`}
            </button>
          ))}
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
                  data-testid="pipeline-campaign-filter" className={`${inputClass} w-auto py-2`}>
            <option value="">All campaigns</option>
            {campaigns.map((campaign) => (
              <option key={campaign.campaign_id} value={campaign.campaign_id}>
                {`${campaign.name} (${campaign.deals})`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {stalled.length ? (
        <div className="flex items-start gap-3 rounded-xl border border-gold/30 bg-gold/5 px-5 py-4"
             data-testid="pipeline-stalled">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          <div>
            <p className="text-sm text-cream/85">{stalled.length} deal(s) have gone quiet for 30+ days</p>
            <p className="mt-1 text-xs leading-relaxed text-cream/50">
              {stalled.slice(0, 4).map((task) => task.title).join(" · ")}
            </p>
          </div>
        </div>
      ) : null}

      {view === "touches" ? (
        <Panel title="Today's touches" testid="panel-today-touches"
               action={<span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
                 {touches.overdue} overdue · {touches.due_today} due · {touches.upcoming} ahead
               </span>}>
          <ul className="divide-y divide-cream/10">
            {touches.touches.map((touch) => (
              <TouchRow key={touch.id} touch={touch}
                        onDone={(t) => closeTouch(t, "done")}
                        onSkip={(t) => closeTouch(t, "skip")} />
            ))}
            {touches.touches.length === 0 ? (
              <li className="py-4 text-sm text-cream/40" data-testid="touches-empty">
                Nothing due. Put a deal on a campaign and the first touch appears here.
              </li>
            ) : null}
          </ul>
        </Panel>
      ) : (
        <div className="-mx-2 flex gap-4 overflow-x-auto px-2 pb-3" data-testid="pipeline-board">
          {board.stages.map((stage) => (
            <div key={stage.key} className="w-[270px] shrink-0"
                 data-testid={`stage-column-${stage.key}`}>
              <div className="mb-3 flex items-baseline justify-between border-b border-cream/10 pb-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/45">
                  {stage.label}
                </p>
                <span className="font-mono text-[10px] text-cream/30">{stage.count}</span>
              </div>
              <ul className="space-y-3">
                {board.by_stage[stage.key].map((deal) => (
                  <DealCard key={deal.id} deal={deal} campaigns={campaigns} onPick={pick}
                            onOpen={setOpenDeal} />
                ))}
                {stage.count === 0 ? (
                  <li className="rounded-xl border border-dashed border-cream/10 px-4 py-6 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-cream/20">
                    Empty
                  </li>
                ) : null}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Panel title="New deal" testid="panel-new-deal">
        <NewDealForm onCreated={load} />
      </Panel>

      {openDeal ? (
        <DealPanel dealId={openDeal.id} campaigns={campaigns} onPick={pick}
                   onClose={() => setOpenDeal(null)} onChanged={load} />
      ) : null}
    </div>
  );
}

const DealPanel = ({ dealId, campaigns, onPick, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [needsReason, setNeedsReason] = useState(false);

  const load = useCallback(() => {
    adminApi.get(`/crm/deals/${dealId}`).then(({ data: payload }) => setData(payload))
      .catch(() => {});
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const { deal, touches, tasks } = data;

  const act = async (fn, message) => {
    try {
      await fn();
      toast.success(message);
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Panel title={deal.name} testid="panel-deal-detail"
           action={<button type="button" onClick={onClose} data-testid="deal-panel-close"
                           className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35 transition-colors duration-200 hover:text-cream">
             Close
           </button>}>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4">
          <CampaignPicker deal={deal} campaigns={campaigns} onPick={onPick} />
          <select value={deal.stage} data-testid="deal-stage-select" className={inputClass}
                  onChange={(e) => {
                    if (e.target.value === "closed_lost" && !deal.lost_reason) {
                      setNeedsReason(true);
                      toast.error("Pick a reason it was lost first");
                      return;
                    }
                    act(() => adminApi.patch(`/crm/deals/${deal.id}`,
                                             { stage: e.target.value }), "Stage updated");
                  }}>
            {campaigns.length && data.deal ? null : null}
            {["new", "appointment_scheduled", "qualified_to_buy", "presentation_scheduled",
              "decision_maker_bought_in", "contract_sent", "closed_won", "closed_lost"]
              .map((stage) => (
                <option key={stage} value={stage}>
                  {stage.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                </option>
              ))}
          </select>
          {deal.stage === "closed_lost" || needsReason || deal.lost_reason ? (
            <select value={deal.lost_reason || ""} className={inputClass}
                    data-testid="deal-lost-reason-select"
                    onChange={(e) => act(() => adminApi.patch(`/crm/deals/${deal.id}`,
                                                             { lost_reason: e.target.value }),
                                         "Lost reason saved")}>
              <option value="" disabled>Why was it lost?</option>
              {(data.lost_reasons || []).map((reason) => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {["Replied", "Meeting booked", "Opted out"].map((kind) => (
              <button key={kind} type="button" data-testid={`deal-log-${kind.split(" ")[0].toLowerCase()}`}
                      onClick={() => act(() => adminApi.post(`/crm/deals/${deal.id}/reply`,
                                                             { kind }), `${kind} logged`)}
                      className="rounded-full border border-cream/15 px-3 py-1.5 text-[11px] text-cream/65 transition-colors duration-200 hover:border-gold/50 hover:text-cream">
                Log {kind.toLowerCase()}
              </button>
            ))}
          </div>
          <dl className="space-y-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/35">
            <div>Renewal: {deal.renewal_date || "—"}</div>
            <div>Effective: {deal.target_effective_date || "—"}</div>
            <div>Last activity: {deal.last_activity_date || "—"}</div>
            <div>Exit reason: {deal.nurture_exit_reason || "—"}</div>
          </dl>
        </div>

        <div className="lg:col-span-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
            Scheduled touches
          </p>
          <ul className="mt-3 divide-y divide-cream/10">
            {touches.map((touch) => (
              <li key={touch.id} className="flex items-center justify-between gap-3 py-2"
                  data-testid={`deal-touch-${touch.id}`}>
                <span className="text-xs text-cream/70">
                  {touch.step_id} · {touch.channel} · {touch.content.slice(0, 54)}
                </span>
                <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${
                  touch.status === "pending" ? "text-cream/40"
                    : touch.status === "done" ? "text-sage" : "text-cream/25"}`}>
                  {touch.status} · {touch.due_date}
                </span>
              </li>
            ))}
            {touches.length === 0 ? (
              <li className="py-2 text-xs text-cream/40">No touches scheduled yet.</li>
            ) : null}
          </ul>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
            Open tasks ({tasks.length})
          </p>
        </div>
      </div>
    </Panel>
  );
};
